<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { updateAccount } from '@gpx-spa/queries';
import HeadingSection from '@/components/_common/Heading/HeadingSection.vue';
import SettingsSettingRow from '@/components/settings/SettingsSettingRow.vue';
import { setTitle } from '@/utils/title';

const store = useStore();
// Separate instances so saving the default never spins the toggle.
const { mutateAsync: mutateEnabled, isPending: isSavingEnabled } =
  updateAccount();
const { mutateAsync: mutateDefault } = updateAccount();

const selectNonce = ref(0);

const defaultOptions = [
  { label: 'Private', value: 'private' },
  { label: 'Public', value: 'public' },
];

const account = computed(() => store.getters['auth/account']);
const accountSettings = computed(() => store.getters['auth/accountSettings']);

const isEnabled = computed(() =>
  store.getters['auth/accountSettingIsOn']('tracker_sharing_enabled'),
);
// Absent setting row is the normal state for an account that has never used the
// feature, and means private.
const currentDefault = computed(() =>
  accountSettings.value.tracker_sharing_default === 'public'
    ? 'public'
    : 'private',
);
const currentDefaultOption = computed(() =>
  defaultOptions.find((option) => option.value === currentDefault.value),
);

const saveSettings = (mutateAsync, settings) =>
  mutateAsync({
    accountId: account.value.id,
    payload: { settings },
  }).then((updatedAccount) => {
    store.dispatch('auth/setAccountSettings', {
      accountId: account.value.id,
      settings: updatedAccount.settings,
    });
  });

const confirm = ({ actionLabel, title, body, onOk }) =>
  store.dispatch('modal/open', {
    name: 'CommonConfirmationModal',
    params: {
      actionLabel,
      title,
      body,
      onOk,
      successMessage: 'Updated!',
      errorMessage: 'Could not update public tracker links',
    },
  });

const onToggleEnabled = (value) => {
  if (value) {
    saveSettings(mutateEnabled, { tracker_sharing_enabled: true });
    return;
  }

  confirm({
    actionLabel: 'Turn off',
    title: 'Public tracker links',
    body: 'Every public link in this account will stop working and show an unavailable page. Nothing is deleted — turning this back on restores the same links, and each tracker keeps its own setting.',
    onOk: () => saveSettings(mutateEnabled, { tracker_sharing_enabled: false }),
  });
};

const onSelectDefault = (option) => {
  // Snap back to the stored value: the change is not applied until confirmed.
  selectNonce.value += 1;

  if (option.value === currentDefault.value) {
    return;
  }

  const isBecomingPublic = option.value === 'public';

  confirm({
    actionLabel: 'Change default',
    title: `Make trackers ${option.label.toLowerCase()} by default`,
    body: isBecomingPublic
      ? 'Every tracker set to "Inherit account default" will get a working public link immediately, including trackers added in the future. Trackers set to Public or Private individually are unaffected. You can change this back at any time.'
      : 'Every tracker set to "Inherit account default" will stop sharing its location, including trackers added in the future. Trackers set to Public or Private individually are unaffected. Nothing is deleted — you can change this back at any time.',
    onOk: () =>
      saveSettings(mutateDefault, { tracker_sharing_default: option.value }),
  });
};

setTitle('Tracker Settings');
</script>

<template>
  <section class="max-w-[860px]">
    <HeadingSection
      class="mb-[35px]"
      desc="Defaults that apply to every tracker on this account."
      heading="Trackers"
    />
    <SettingsSettingRow
      title="Public tracker links"
      desc="When on, every tracker in this account has a public link to a live view of its location. Each tracker can override this on its own page."
    >
      <div class="flex items-center gap-x-[10px] sm:justify-end">
        <UISpinnerNew v-if="isSavingEnabled" size="sm" />
        <UIToggle
          :modelValue="isEnabled"
          :disabled="isSavingEnabled"
          aria-label="Public tracker links"
          @update:modelValue="onToggleEnabled"
        />
      </div>
    </SettingsSettingRow>
    <SettingsSettingRow
      v-if="isEnabled"
      title="Default for trackers"
      desc='Applies to every tracker set to "Inherit account default"'
    >
      <UISelect
        :key="`${currentDefault}-${selectNonce}`"
        aria-label="Default for trackers"
        placeholder="Select a default"
        :options="defaultOptions"
        :current="currentDefaultOption"
        no-caret
        @onSelect="onSelectDefault"
      />
    </SettingsSettingRow>
  </section>
</template>
